import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@df/ui/translation/translate.pipe';
import { parseNumber } from '@df/util/fn/parse-number';

@Component({
    selector: 'multi-file-upload',
    templateUrl: './multi-file-upload.html',
    styleUrls: ['./multi-file-upload.scss'],
    standalone: true,
    imports: [CommonModule, FormsModule, TranslatePipe]
})
export class MultiFileUploadComponent {
    @Input()
    files: File[] = [];

    @Input()
    label: string = '';

    @Input()
    name: string = '';

    @Input()
    required: boolean = false;

    @Input()
    accept?: string;

    @Input()
    maxSize?: string | number;

    @Input()
    maxFiles?: number;

    @Input()
    dragDropText?: string;

    @Input()
    openBrowserText?: string;

    @Output()
    filesChange = new EventEmitter<File[]>();

    protected isDragOver = false;
    protected errors: string[] = [];

    protected onFileSelect(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files) {
            this.addFiles(Array.from(input.files));
            // Clear the input so the same file can be selected again
            input.value = '';
        }
    }

    protected onDragOver(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = true;
    }

    protected onDragLeave(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;
    }

    protected onDrop(event: DragEvent): void {
        event.preventDefault();
        event.stopPropagation();
        this.isDragOver = false;

        if (event.dataTransfer?.files) {
            this.addFiles(Array.from(event.dataTransfer.files));
        }
    }

    private addFiles(newFiles: File[]): void {
        this.errors = [];
        const validFiles: File[] = [];

        for (const file of newFiles) {
            const validationResult = this.validateFile(file);
            if (validationResult.isValid) {
                validFiles.push(file);
            } else {
                this.errors.push(...validationResult.errors);
            }
        }

        // Check file number limit after adding valid files
        const totalFiles = this.files.length + validFiles.length;
        if (this.maxFiles && totalFiles > this.maxFiles) {
            const allowedCount = this.maxFiles - this.files.length;
            if (allowedCount > 0) {
                validFiles.splice(allowedCount);
                this.errors.push(`You can only upload up to ${this.maxFiles} files. Only the first ${allowedCount} files were added.`);
            } else {
                this.errors.push(`You can only upload up to ${this.maxFiles} files. No files were added.`);
                return;
            }
        }

        if (validFiles.length > 0) {
            const updatedFiles = [...this.files, ...validFiles];
            this.files = updatedFiles;
            this.filesChange.emit(this.files);
        }
    }

    protected removeFile(index: number): void {
        const updatedFiles = this.files.filter((_, i) => i !== index);
        this.files = updatedFiles;
        this.filesChange.emit(this.files);
    }

    protected formatFileSize(bytes: number): string {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    }

    private validateFile(file: File): { isValid: boolean; errors: string[] } {
        const errors: string[] = [];

        // Validate file size
        if (this.maxSize) {
            const maxSizeBytes = typeof this.maxSize === 'string' ? parseNumber(this.maxSize) : this.maxSize;
            if (file.size > maxSizeBytes) {
                const maxSizeFormatted = typeof this.maxSize === 'string' ? this.maxSize : this.formatFileSize(this.maxSize);
                errors.push(`File "${file.name}" is too large. Maximum size is ${maxSizeFormatted}.`);
            }
        }

        // Validate file type
        if (this.accept) {
            const fileType = file.type?.toLowerCase();
            const fileName = file.name?.toLowerCase();

            if (!this.isFileTypeAllowed(fileType, fileName)) {
                errors.push(`File "${file.name}" has an invalid format. Allowed formats: ${this.accept}.`);
            }
        }

        return {
            isValid: errors.length === 0,
            errors
        };
    }

    private isFileTypeAllowed(fileType: string, fileName: string): boolean {
        if (!this.accept) return true;

        const acceptedTypes = this.accept
            .toLowerCase()
            .split(',')
            .map(type => type.trim());

        for (const acceptedType of acceptedTypes) {
            // Check MIME type (e.g., "image/jpeg")
            if (acceptedType.includes('/')) {
                if (acceptedType.includes('*')) {
                    // Handle wildcards like "image/*"
                    const baseType = acceptedType.split('/')[0];
                    if (fileType.startsWith(baseType + '/')) {
                        return true;
                    }
                } else if (fileType === acceptedType) {
                    return true;
                }
            }
            // Check file extension (e.g., ".jpg", "jpg")
            else {
                const extension = acceptedType.startsWith('.') ? acceptedType : '.' + acceptedType;
                if (fileName.endsWith(extension)) {
                    return true;
                }
            }
        }

        return false;
    }
}
